import * as safeRegexModule from "safe-regex2";

// safe-regex2 is CommonJS (`module.exports = fn`): the function is the default under Node ESM
// and bundlers, or the namespace itself under some interop modes.
const safeRegex = ((safeRegexModule as unknown as { default?: unknown }).default ??
  safeRegexModule) as unknown as (re: string | RegExp) => boolean;

/** Rule patterns run on every imported row, so the schema's 200-character cap applies here too. */
export const maxRulePatternLength = 200;

/**
 * Why a user-written regex can't be used as a payee rule, or null when it can. Patterns with
 * nested repetition (`(\w+\s?)*`) can backtrack for minutes on one description (ReDoS).
 */
export function regexRuleProblem(pattern: string): string | null {
  if (pattern.length > maxRulePatternLength) {
    return `The rule's pattern is longer than ${maxRulePatternLength} characters`;
  }
  let re: RegExp;
  try {
    re = new RegExp(pattern, "i");
  } catch {
    return "The rule's pattern is not a valid regular expression";
  }
  if (!safeRegex(re) || hasRepeatedAmbiguousGroup(pattern)) {
    return "The rule's pattern could be too slow to run (nested repetition such as (a+)*); simplify it";
  }
  return null;
}

interface GroupScan {
  alternation: boolean;
  repetition: boolean;
}

/**
 * True when a group containing alternation or repetition is itself repeated (`*`, `+`, `{n,}`,
 * `{n,m}`): `(a|aa)+$` backtracks exponentially, and safe-regex2's star-height check misses it.
 * `?` and `{n}` don't repeat unboundedly, so they are allowed.
 */
function hasRepeatedAmbiguousGroup(pattern: string): boolean {
  const stack: GroupScan[] = [{ alternation: false, repetition: false }];
  let i = 0;
  while (i < pattern.length) {
    const c = pattern[i];
    const top = stack[stack.length - 1] as GroupScan;
    if (c === "\\") {
      i += 2;
      continue;
    }
    if (c === "[") {
      i = skipClass(pattern, i);
      continue;
    }
    if (c === "(") {
      stack.push({ alternation: false, repetition: false });
      i += 1;
      continue;
    }
    if (c === "|") {
      top.alternation = true;
      i += 1;
      continue;
    }
    if (c === ")" && stack.length > 1) {
      stack.pop();
      const repeats = repetitionAt(pattern, i + 1);
      if (repeats && (top.alternation || top.repetition)) {
        return true;
      }
      const parent = stack[stack.length - 1] as GroupScan;
      parent.repetition ||= top.repetition || repeats;
      i += 1;
      continue;
    }
    if (repetitionAt(pattern, i)) {
      top.repetition = true;
    }
    i += 1;
  }
  return false;
}

/** True when an unbounded or ranged quantifier starts at `i`. */
function repetitionAt(pattern: string, i: number): boolean {
  const c = pattern[i];
  if (c === "*" || c === "+") {
    return true;
  }
  return c === "{" && /^\{\d+,\d*\}/.test(pattern.slice(i));
}

/** The index just after the character class starting at `start`. */
function skipClass(pattern: string, start: number): number {
  let i = start + 1;
  if (pattern[i] === "]") {
    i += 1;
  }
  while (i < pattern.length && pattern[i] !== "]") {
    i += pattern[i] === "\\" ? 2 : 1;
  }
  return i + 1;
}
